/**
 * Owner: Masa Bayu
 * File: PrivateAuthorSummary.tsx
 * Date:Time: 2026-10-23 08:55
 * Description: Tab Ringkasan §467-C — 4 kad KPI daripada `/v1/publisher/dashboard`
 *   (jualan + buku = nyata; royalti + pengikut = "tiada data" kerana hub belum
 *   ada sumber — tiada lencana DEMO, tiada nilai karangan), carta jualan sebenar,
 *   donut hasil ikut mata wang, karusel Buku Saya milik akaun, Transaksi Terbaru
 *   (pesanan sebenar) dan Ringkasan Katalog. Kad Ulasan + Pencapaian demo dibuang.
 */

import type { ReactElement, ReactNode } from 'react';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import type { MarketplaceLocaleProfile } from '../../home/marketplace/marketplace-locale';
import { CurrencyDonut, SalesLineChart } from './PrivateAuthorCharts';
import { PrivateAuthorBooks } from './PrivateAuthorBooks';
import type { AuthorDashboardModel, AuthorKpi, AuthorSeriesPoint } from './private-author-view';
import type { PrivateAuthorLabels, PrivateKpiId } from './private-author-labels';

const KPI_TONES = ['#e8f1ff', '#eaf8ef', '#fff4e6', '#f3e8ff'];

function Stroke({
  children,
  size = 18,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const KPI_ICONS: Record<PrivateKpiId, ReactElement> = {
  sales: (
    <Stroke>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18M16 6V4h-2" />
    </Stroke>
  ),
  royalty: (
    <Stroke>
      <circle cx="12" cy="12" r="8" />
      <path d="M9.5 14.5c2 1.5 5 1 5-1 0-2.5-5-1.5-5-4 0-2 3-2.5 5-1" />
    </Stroke>
  ),
  books: (
    <Stroke>
      <path d="M12 6c-1.6-1.3-3.6-2-6-2v14c2.4 0 4.4.7 6 2 1.6-1.3 3.6-2 6-2V4c-2.4 0-4.4.7-6 2z" />
      <path d="M12 6v14" />
    </Stroke>
  ),
  followers: (
    <Stroke>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 19a6 6 0 0 1 12 0" />
      <path d="M16 5.5a3 3 0 0 1 0 5.5M17 19a6 6 0 0 0-2-4.3" />
    </Stroke>
  ),
};

function KpiCard({
  kpi,
  index,
  la,
}: {
  readonly kpi: AuthorKpi;
  readonly index: number;
  readonly la: PrivateAuthorLabels;
}): ReactElement {
  return (
    <article className="qxp__kpi">
      <p className="qxp__kpi-title">
        <span
          className="qxp__kpi-ic"
          style={{ background: KPI_TONES[index % KPI_TONES.length] ?? '#e8f1ff' }}
          aria-hidden="true"
        >
          {KPI_ICONS[kpi.id]}
        </span>
        {la.kpiTitles[kpi.id]}
      </p>
      <strong className="qxp__kpi-value">{kpi.value}</strong>
      {!kpi.real ? (
        <p className="qxp__kpi-delta">
          <span className="qxp__kpi-soft">{la.noData}</span>
        </p>
      ) : kpi.deltaPct === null ? (
        <p className="qxp__kpi-delta">{'\u00A0'}</p>
      ) : (
        <p className="qxp__kpi-delta">
          <span className={kpi.deltaPct >= 0 ? 'qxp__kpi-up' : 'qxp__kpi-down'}>
            {kpi.deltaPct >= 0 ? '↑' : '↓'} {String(Math.abs(kpi.deltaPct))}%
          </span>{' '}
          {la.kpiDelta}
        </p>
      )}
    </article>
  );
}

export interface PrivateAuthorSummaryProps {
  readonly onNavigate: (path: string) => void;
  readonly profile: MarketplaceLocaleProfile;
  readonly la: PrivateAuthorLabels;
  readonly model: AuthorDashboardModel;
  readonly series: readonly AuthorSeriesPoint[];
}

export function PrivateAuthorSummary({
  onNavigate,
  profile,
  la,
  model,
  series,
}: PrivateAuthorSummaryProps): ReactElement {
  const catalog = [
    { label: la.catalogBooks, value: String(model.booksPublished) },
    { label: la.catalogPaid, value: String(model.paidOrders) },
    { label: la.catalogBuyers, value: String(model.buyers) },
    { label: la.catalogPending, value: String(model.pendingOrders) },
  ];

  return (
    <div className="qxp__summary">
      <div className="qxp__kpis">
        {model.kpis.map((kpi, i) => (
          <KpiCard key={kpi.id} kpi={kpi} index={i} la={la} />
        ))}
      </div>

      <div className="qxp__charts">
        <SalesLineChart la={la} series={series} currency={model.currency} />
        <CurrencyDonut la={la} rows={model.revenueRows} />
      </div>

      <PrivateAuthorBooks
        onNavigate={onNavigate}
        la={la}
        books={model.books}
        language={profile.language}
      />

      <div className="qxp__lower">
        <section className="qxp__card qxp__orders" id="qxp-transaksi" aria-label={la.ordersTitle}>
          <div className="qxp__card-head">
            <h2 className="qxp__card-title">{la.ordersTitle}</h2>
            <div className="qxp__card-tools">
              <button
                type="button"
                className="qxp__seeall"
                onClick={() => {
                  onNavigate('/author/earnings');
                }}
              >
                {la.seeAll}
              </button>
            </div>
          </div>
          {model.orders.length === 0 ? (
            <p className="qxp__empty">{la.ordersEmpty}</p>
          ) : (
            <ul>
              {model.orders.map((order) => (
                <li key={order.orderId}>
                  <span className="qxp__order-ic" aria-hidden="true">
                    <LandingIcon name="receipt" size={16} />
                  </span>
                  <div className="qxp__order-main">
                    <p className="qxp__order-title">
                      <strong>{order.title}</strong>
                      <em>{order.state}</em>
                    </p>
                    <p className="qxp__order-meta">
                      {order.createdAt === null ? model.noData : order.createdAt.slice(0, 10)}
                    </p>
                  </div>
                  <strong className="qxp__order-amount">{order.amountText}</strong>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="qxp__card qxp__catalog" aria-label={la.catalogTitle}>
          <h2 className="qxp__card-title">{la.catalogTitle}</h2>
          <ul>
            {catalog.map((row) => (
              <li key={row.label}>
                <span>{row.label}</span>
                <strong>{row.value}</strong>
              </li>
            ))}
            <li>
              <span>{la.currencyTitle}</span>
              <strong>{model.revenueText}</strong>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
